/**
 * Klartid — delade typer for klient och server. Speglar bada databaserna
 * rad for rad (aven infrastrukturtabellerna) och varje kropp som gar over
 * natet. snake_case overallt, aven pa natet: ett begrepp = ett namn, sa
 * work_date heter work_date i SQL, JSON och TypeScript och ett
 * datumintervall heter from_date/to_date i bada andar av kabeln. Inga
 * `any`. Enheter, rundningsregler, versionstrappan, mjuk radering och
 * veckans harledning definieras i schema/company.sql och upprepas inte
 * har. ALLA BELOPP AR EX MOMS OCH FORE ROT.
 * Filen innehaller EN sorts kord kod: enhetskonstruktorerna i avsnitt 1.
 */

import type { ApiErrorBody, PriceMissingDetail } from './errors';

/* --- 1. Markta enheter --- */

/** Fantomsymbol. Finns aldrig i kord kod och ska aldrig importeras. */
export declare const BRAND: unique symbol;

export type Brand<TBase, TName extends string> = TBase & {
  readonly [BRAND]: TName;
};

export type Minutes = Brand<number, 'Minutes'>;
/**
 * Heltal oren, ett BELOPP. Ore anvands ALDRIG for en sats: timpris ar
 * OrePerHour, milersattning OrePer10Km, materialpris per enhet
 * OrePerUnit. Med ett enda marke kunde `total_ore: row.rate_ore_per_hour`
 * kompilera utan ett knyst.
 */
export type Ore = Brand<number, 'Ore'>;
export type OrePerHour = Brand<number, 'OrePerHour'>;
export type OrePer10Km = Brand<number, 'OrePer10Km'>;
export type OrePerUnit = Brand<number, 'OrePerUnit'>;
export type Meters = Brand<number, 'Meters'>;
export type Milli = Brand<number, 'Milli'>;
export type Millis = Brand<number, 'Millis'>;
export type Hundredths = Brand<number, 'Hundredths'>;
export type WorkDate = Brand<string, 'WorkDate'>;
/**
 * ISO-vecka 'YYYY-Www' (mandag forst). YYYY ar ISO-VECKOARET, aldrig
 * kalenderaret i arbetsdagen. Enda periodbegreppet i tidrapporteringen
 * (beslut 04).
 */
export type IsoWeek = Brand<string, 'IsoWeek'>;
// INGEN KALENDERMANADSTYP FINNS (beslut 18): faktureringen loper i
// ANKARPERIODER raknade fran den dag firman signade, och en period bars av
// tva Millis (billing_period.period_start / period_end). Ett 'YYYY-MM' hade
// bara kunnat uttrycka en period som borjar den 1:a — och en sadan period
// finns inte i produkten. Radkommentar och inte /** */ med flit: blocket
// dokumenterar en typ som INTE finns, sa ett doc-block hade knutits till
// nasta deklaration i filen och pastatt nagot om den i stallet.
/** Radversion for optimistisk lasning vid synk (beslut 03). */
export type Version = Brand<number, 'Version'>;
export type Seq = Brand<number, 'Seq'>;

/**
 * KONSTRUKTORER — enda tillatna vagen in i en markt enhet. Ett bart
 * `as Minutes` / `as Ore` / ... far INTE forekomma i klient- eller
 * serverkod: allt som kommer ur JSON.parse eller ur en D1-/DO-rad passerar
 * dessa. Utan dem slutar markningen verka precis dar den behovs — vid
 * natgransen och vid databaslasningen.
 */
const safeInt = (n: unknown, unit: string): number => {
  if (typeof n !== 'number' || !Number.isSafeInteger(n)) {
    throw new Error(`${unit} maste vara ett heltal, fick ${String(n)}`);
  }
  return n;
};

const matches = (s: unknown, re: RegExp, unit: string): string => {
  if (typeof s !== 'string' || !re.test(s)) {
    throw new Error(`${unit} har fel form, fick ${String(s)}`);
  }
  return s;
};

export const asMinutes = (n: unknown): Minutes => safeInt(n, 'Minutes') as Minutes;
export const asOre = (n: unknown): Ore => safeInt(n, 'Ore') as Ore;
export const asOrePerHour = (n: unknown): OrePerHour =>
  safeInt(n, 'OrePerHour') as OrePerHour;
export const asOrePer10Km = (n: unknown): OrePer10Km =>
  safeInt(n, 'OrePer10Km') as OrePer10Km;
export const asOrePerUnit = (n: unknown): OrePerUnit =>
  safeInt(n, 'OrePerUnit') as OrePerUnit;
export const asMeters = (n: unknown): Meters => safeInt(n, 'Meters') as Meters;
export const asMilli = (n: unknown): Milli => safeInt(n, 'Milli') as Milli;
export const asMillis = (n: unknown): Millis => safeInt(n, 'Millis') as Millis;
export const asHundredths = (n: unknown): Hundredths =>
  safeInt(n, 'Hundredths') as Hundredths;
export const asVersion = (n: unknown): Version => safeInt(n, 'Version') as Version;
export const asSeq = (n: unknown): Seq => safeInt(n, 'Seq') as Seq;
export const asWorkDate = (s: unknown): WorkDate =>
  matches(s, /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'WorkDate') as WorkDate;
export const asIsoWeek = (s: unknown): IsoWeek =>
  matches(s, /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/, 'IsoWeek') as IsoWeek;

/* --- 2. Id-typer --- */

export type AccountId = Brand<string, 'AccountId'>;
export type CompanyId = Brand<string, 'CompanyId'>;
export type CustomerId = Brand<string, 'CustomerId'>;
export type WorkOrderId = Brand<string, 'WorkOrderId'>;
export type WorkOrderGuestId = Brand<string, 'WorkOrderGuestId'>;
export type WorkOrderPhotoId = Brand<string, 'WorkOrderPhotoId'>;
export type TaskId = Brand<string, 'TaskId'>;
export type TimeEntryId = Brand<string, 'TimeEntryId'>;
export type MaterialLineId = Brand<string, 'MaterialLineId'>;
export type MileageId = Brand<string, 'MileageId'>;
export type ExpenseId = Brand<string, 'ExpenseId'>;
export type AbsenceId = Brand<string, 'AbsenceId'>;
export type PriceListId = Brand<string, 'PriceListId'>;
export type PriceRateId = Brand<string, 'PriceRateId'>;
export type InvoiceRunId = Brand<string, 'InvoiceRunId'>;
export type AuditLogId = Brand<string, 'AuditLogId'>;
export type StripeEventId = Brand<string, 'StripeEventId'>;
/** Id pa en enskild synkoperation. Idempotensnyckeln (beslut 09). */
export type OpId = Brand<string, 'OpId'>;
export type DeviceId = Brand<string, 'DeviceId'>;

/**
 * Nyckel till en fil i R2, formen '<company_id>/<kind>/<ulid>.<ext>' dar
 * kind ar 'photo' eller 'receipt'. GENERERAS PA KLIENTEN nar bilden tas,
 * som alla andra id — det ar vad som gor beslut 13 mojligt i en kallare:
 * fotoraden laggs i utkorgen direkt och bytesen laddas upp nar natet
 * kommer tillbaka. FORSTA SEGMENTET AR EN BEHORIGHETSGRANS: FILE_UPLOAD
 * avvisar en nyckel vars forsta segment inte ar sokvagens :company_id, och
 * FILE_DOWNLOAD gor samma kontroll fore utlamning. Ogissbarhet ar aldrig
 * behorighet — annars kunde en bokforare som ar medlem i bade A och B
 * hamta B:s kvittofoton genom A:s sokvag.
 */
export type ObjectKey = Brand<string, 'ObjectKey'>;

export type OrgNumber = Brand<string, 'OrgNumber'>;

export const SYSTEM_ACTOR = 'system';

export type Actor = AccountId | typeof SYSTEM_ACTOR;

/* --- 3. Vardemangder --- */

/**
 * manager    chefen, ser "Laget", far skriva vems rad som helst
 * worker     montoren, ser bara "Mitt jobb"
 * apprentice larlingen, samma vy, eget timpris
 * guest      inhyrd, gratis, eget org.nr (beslut 17), ser bara tilldelade
 *            arbetsordrar
 * bookkeeper bokforaren, laser allt, andrar aldrig nagot, gratis
 * ROLLEN LASES ALLTID UR D1 (membership), aldrig ur licensbeviset.
 */
export type Role =
  | 'manager'
  | 'worker'
  | 'apprentice'
  | 'guest'
  | 'bookkeeper';

/** Roller som far ha timpris (beslut 15). Bokforaren rapporterar inte. */
export type PriceRole = Exclude<Role, 'bookkeeper'>;
export type ReportingRole = PriceRole;

/** Ren typniva: T maste vara true, annars faller tsc pa constraintet. */
type Assert<TTrue extends true> = TTrue;

/**
 * SPARR MOT DIVERGENS. shared/errors.ts importerar ingenting (den ar botten i
 * beroendekedjan) och maste darfor skriva ut PRICE_MISSING:s rollmangd som en
 * literalunion i PriceMissingDetail. Den har raden bevisar att mangden ar
 * EXAKT PriceRole: laggs en roll till eller tas en bort pa ena stallet blir
 * villkoret false och tsc faller har, i stallet for att de tva tyst glider
 * isar. Ingen kord kod, bara en typkontroll.
 */
export type PriceMissingRoleIsPriceRole = Assert<
  [PriceRole] extends [PriceMissingDetail['role']]
    ? [PriceMissingDetail['role']] extends [PriceRole]
      ? true
      : false
    : false
>;
/**
 * BETALDA ROLLER (beslut 18): de som kostar PRICE_PER_USER_ORE per period
 * enbart genom att vara UPPLAGDA i firman — oavsett om de rapporterat nagot.
 * Gasten ar gratis (beslut 17: han ar anstalld i ett annat foretag) och
 * bokforaren ar gratis (beslut 07: han ar utomstaende). Skild fran PriceRole
 * med flit: gasten HAR ett timpris mot kundens kund men kostar inget hos
 * Klartid, sa de tva mangderna svarar pa olika fragor och far aldrig slas
 * ihop.
 */
export type PaidRole = Exclude<Role, 'guest' | 'bookkeeper'>;
export type FullViewRole = 'manager' | 'bookkeeper';
export type ClientView = 'full' | 'worker' | 'guest';

/** Rollens vy som data, sa att beslut 16 och 17 inte kan divergera. */
export const ROLE_VIEW = {
  manager: 'full',
  bookkeeper: 'full',
  worker: 'worker',
  apprentice: 'worker',
  guest: 'guest',
} as const satisfies Record<Role, ClientView>;

/**
 * Fakturans rollmangd som DATA (beslut 18), sa att stangningens rakning,
 * SUBSCRIPTION:s preliminara tal och prosan pa billing_period i
 * schema/d1.sql inte kan divergera. Rakningen ar hela fragan:
 *   SELECT COUNT(*) FROM member
 *    WHERE deleted_at IS NULL AND role IN (...PAID_ROLES);
 */
export const PAID_ROLES = [
  'manager',
  'worker',
  'apprentice',
] as const satisfies readonly PaidRole[];

export type CustomerKind = 'private' | 'company';
export type WorkOrderStatus = 'open' | 'done';
export type EntrySource = 'manual' | 'voice';
/** Beslut 15: vilken niva som gav det frysta priset. */
export type RateSource = 'work_order' | 'customer' | 'company';
/** Informationsfalt. Paverkar aldrig milbeloppet. */
export type Vehicle = 'own_car' | 'company_car';

/**
 * Franvarons ORSAK ar med flit begransad till icke-kansliga varden. Har
 * fanns forut tva slag till, som sa VARFOR nagon var borta. Bada var
 * uppgifter om halsa — sarskild kategori enligt artikel 9 GDPR — och de togs
 * bort: de drog in hela artikel 9 over tjansten utan att fylla nagon
 * funktion, for beslut 01 gor Klartid till FAKTURAUNDERLAG, inte
 * loneunderlag, och ett fakturaunderlag bryr sig inte om VARFOR nagon var
 * borta, bara om att dagen inte ska raknas som saknad tid. Den som anda
 * behover notera nagot specifikt skriver det i tidradens anteckningsfalt.
 * HELA SKALET, med de borttagna vardena utskrivna, star over tabellen
 * absence i schema/company.sql. Las det innan du lagger till ett slag har.
 */
export type AbsenceKind = 'vacation' | 'leave' | 'other';

/**
 * 'h' (timme) finns med FLIT INTE: en timenhet skulle gora unit_price_ore
 * till ett timpris och quantity_milli till tusendelar av en timme — bade
 * en andra vag att rapportera arbetstid i fel enhet och en vag forbi
 * prislistan rakt ut till varje montors telefon (beslut 15 och 16).
 */
export type MaterialUnit = 'pcs' | 'm' | 'm2' | 'm3' | 'kg' | 'l' | 'pkg';

export type WeekState =
  | 'open'
  | 'submitted'
  | 'approved'
  | 'locked'
  | 'reopened';

export const EDITABLE_WEEK_STATES = [
  'open',
  'submitted',
  'reopened',
] as const satisfies readonly WeekState[];

/**
 * BESLUT 10, andra halvan: en manager far dessutom skriva i SIN EGEN vecka
 * nar den star i 'approved' (dit den satts redan vid hans forsta
 * sparning). Utan mangden kunde han spara exakt en rad per vecka och sedan
 * vara utelast ur sin egen vecka; forst 'locked' stanger honom ute.
 */
export const MANAGER_OWN_WEEK_WRITABLE_STATES = [
  'open',
  'submitted',
  'reopened',
  'approved',
] as const satisfies readonly WeekState[];

/**
 * ATTESTERAT i fakturaunderlaget (beslut 05) och kravet for att en rad ska
 * kunna stamplas av en invoice_run. 'reopened' raknas INTE.
 */
export const APPROVED_WEEK_STATES = [
  'approved',
  'locked',
] as const satisfies readonly WeekState[];

export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'locked';

/* --- 4. Konstanter: tal som annars hade blivit gissningar --- */

export const LICENSE_TTL_DAYS = 30;
/**
 * Gratisdagar fran signeringsdagen (beslut 18). Firman som signar den 20:e
 * dras forsta gangen den 27:e, och DEN dagen blir firmans ankare for all
 * framtid. Talet ar alltsa inte bara en provperiod utan ocksa det som satter
 * betalningsdagen.
 */
export const TRIAL_DAYS = 7;
export const OP_LOG_RETENTION_DAYS = 45;
/**
 * SA LANGE EN UPPSAGD FIRMAS UPPGIFTER SPARAS, raknat fran uppsagningsdagen
 * (subscription.locked_at i schema/d1.sql). Villkoren punkt 10.5 och 10.6:
 * "Vi sparar firmans uppgifter i 12 manader raknat fran den dag den sista
 * betalda perioden tog slut", och efter det raderas de.
 * VARFOR 366 DAGAR OCH INTE "12 MANADER": tjansten har ingen kalendermanad
 * som periodbegrepp (beslut 04 och 18), och en manadsrakning behover dessutom
 * svara pa vad 12 manader efter den 29 februari ar. 366 dagar ar ALDRIG
 * KORTARE an tolv manader, sa gransen kan bara falla PA eller EFTER
 * arsdagen — aldrig fore. Riktningen ar vald: punkt 10.5 lovar kunden att
 * han kan logga in, hamta ut allt och teckna om under HELA tiden, och en
 * gallring som raderade en dag for tidigt hade brutit det loftet mot en kund
 * som stod med exporten i handen. Att spara en dag for lange bryter inget
 * lofte alls.
 */
export const DATA_RETENTION_DAYS = 366;
/**
 * Priset per UPPLAGD anvandare med en betald roll (PAID_ROLES) och
 * ankarperiod, ex moms (beslut 18). Inte per rapporterande anvandare: den
 * som ar upplagd kostar aven en manad da han inte rort appen.
 */
export const PRICE_PER_USER_ORE = asOre(11900);
export const MAX_OPS_PER_PUSH = 200;
export const SYNC_PULL_DEFAULT_LIMIT = 500;
export const SYNC_PULL_MAX_LIMIT = 1000;
export const WORK_DATE_MAX_DAYS_BACK = 400;
export const WORK_DATE_MAX_DAYS_AHEAD = 7;
export const INVOICE_BASIS_MAX_DAYS = 366;
export const INVOICE_BASIS_MAX_LINES = 5000;
export const MAX_UPLOAD_BYTES = 6_000_000;
/**
 * ROSTEN (beslut 02). Taket pa ljudet i AVKODADE bytes; over det: FILE_TOO_LARGE
 * { max_bytes }. En rostrapport ar en mening pa tio-tjugo sekunder, och
 * MAX_VOICE_SECONDS ar den tid klienten spelar in som langst innan den stoppar
 * sjalv. Opus (webm/ogg) ger ~0,25-1 MB per minut och AAC (mp4) ~1 MB, sa
 * 2 MB rymmer en hel minut med marginal och stanger dorren for den som skulle
 * skicka en timmes inspelning genom tolkningen — som kostar per minut.
 */
export const MAX_VOICE_AUDIO_BYTES = 2_000_000;
export const MAX_VOICE_SECONDS = 60;
/**
 * INLOGGNINGSKODEN (avsnitt 9). Talen star har for att BREVET, KLIENTENS
 * FALT och SERVERNS HARLEDNING ska lasa samma sak: sex siffror, tio minuter,
 * fem forsok. LOGIN_CODE_TTL_MINUTES ar tidsfonstrets langd; en kod godtas i
 * sitt eget fonster OCH det foregaende, sa den lever i praktiken 10-20
 * minuter, och brevet lovar det kortare talet. LOGIN_CODE_MAX_ATTEMPTS ar
 * serverns sparr per adress och minut (LOGIN_RATE_LIMIT i wrangler.jsonc);
 * klienten raknar ner mot samma tal sa att "3 forsok kvar" ar sant.
 */
export const LOGIN_CODE_DIGITS = 6;
export const LOGIN_CODE_TTL_MINUTES = 10;
export const LOGIN_CODE_MAX_ATTEMPTS = 5;
export const AUTH_HEADER = 'Authorization';
export const AUTH_SCHEME = 'Bearer';
export const OP_ID_HEADER = 'X-Op-Id';
export const STRIPE_SIGNATURE_HEADER = 'Stripe-Signature';
export const CRON_SECRET_HEADER = 'X-Klartid-Cron';

/* --- 5. Rader i den delade D1-databasen --- */

export interface AccountRow {
  id: AccountId;
  email: string;
  name: string;
  created_at: Millis;
  updated_at: Millis;
}

export interface CompanyRow {
  id: CompanyId;
  name: string;
  org_number: OrgNumber;
  /** Durable Object-id. Lamnar aldrig servern. */
  do_id: string;
  owner_account_id: AccountId;
  created_at: Millis;
  updated_at: Millis;
}

export interface MembershipRow {
  company_id: CompanyId;
  account_id: AccountId;
  role: Role;
  guest_org_number: OrgNumber | null;
  created_at: Millis;
  updated_at: Millis;
}

export interface SubscriptionRow {
  company_id: CompanyId;
  stripe_customer_id: string;
  stripe_subscription_id: string | null;
  /**
   * Prenumerationens EGNA aterkommande pris, som bara driver cykeln och
   * ankaret. MASTE vara 0 oren — hela beloppet bars av stangningens
   * engangspost (formen star pa subscription i schema/d1.sql). NULL tills
   * BILLING_CHECKOUT ar gjord.
   */
  stripe_price_id: string | null;
  status: SubscriptionStatus;
  /**
   * Den lopande periodens gransar, ms UTC. CREATE_COMPANY skriver nu och
   * nu + TRIAL_DAYS; darefter satts BADA om ur ABONNEMANGSRADENS
   * period.start/period.end (lines.data[] med subscription satt) pa varje
   * betald faktura (invoice.paid), som ar enda vagen som flyttar dem.
   * ALDRIG ur fakturans egna period_start/period_end: fakturahuvudet ser
   * bakat ett varv och skulle lasa ankaret vid det som just passerade.
   * KOLUMNERNA HAR EXAKT TVA SKRIVARE, CREATE_COMPANY en gang och
   * invoice.paid darefter. BILLING_CHECKOUT ror dem aldrig — den LASER
   * current_period_end och lamnar talet vidare som Stripes trial_end (och
   * vid omteckning efter ett las utan trial_end alls, se BILLING_CHECKOUT i
   * shared/routes.ts).
   * current_period_end AR FIRMANS ANKARE (beslut 18): samma tal ar bade
   * nasta dragning och gransen mellan tva perioder, och det finns ingen
   * separat ankarkolumn. Utan invoice.paid-vagen star en betalande firma
   * kvar pa provperiodens slutdatum for alltid — och da fyrar bade
   * provperiodssvepet och stangningens per-firma-villkor pa skrap.
   *
   * MONOTONICITETEN: ANKARET GAR BARA FRAMAT. invoice.paid skriver de har
   * kolumnerna ENDAST nar abonnemangsradens period.end ar STORRE an det
   * lagrade current_period_end. Ar den lika eller mindre ar handelsen en
   * omleverans eller kom i fel ordning: kolumnerna lamnas ORORDA (status
   * satts anda till 'active'), och handelsen loggas och kastas.
   * VARFOR, OCH VARFOR TYSTNADEN AR SKADAN: Stripe garanterar ingen
   * leveransordning for webhooks och levererar om vid fel. Ett ankare som
   * gar bakat pekar tillbaka pa en period som REDAN har en rad i
   * billing_period — da ar stangningens NOT EXISTS-villkor uppfyllt for
   * alltid, firman valjs aldrig igen, och dess fakturering dor permanent
   * utan att nagot kraschar, utan felkod och utan larm. Det ar just
   * tystnaden som ar farlig, inte skrivningen; darfor loggas den kastade
   * handelsen.
   *
   * STANG FORE FLYTT: bar ankarfakturans abonnemangsrad ett period.start
   * som saknar rad i billing_period utloser invoice.paid FORST
   * CLOSE_BILLING_PERIOD for det period_start, och flyttar kolumnerna
   * FORST NAR raden finns. Annars racker en tappad invoice.created for att
   * en betalande firmas period ska hoppas over for gott: abonnemangets eget
   * pris ar 0 oren, sa nollfakturan betalar sig sjalv och ankaret gar
   * vidare. Hela regeln med skalen star pa subscription i schema/d1.sql.
   */
  current_period_start: Millis;
  current_period_end: Millis;
  updated_at: Millis;
}

/**
 * Prenumerationen som den lamnar servern. Beslut 07: bokforaren ar
 * UTOMSTAENDE och verksam i flera firmor, och SUBSCRIPTION ar oppen for
 * FULL_VIEW_ROLES. Det som skalas bort ar firmans BETALNINGSIDENTITET: de
 * tre handtag som pekar ut sjalva kundforhallandet hos Stripe
 * (stripe_customer_id, stripe_subscription_id, stripe_price_id) och som
 * anvands for att administrera eller debitera det.
 * DE TRE lamnar aldrig servern i nagot svar.
 * REGELN GALLER DE TRE, inte varje strang som borjar pa 'stripe_'. Ett
 * Stripe-id gar ut med flit: billing_period.stripe_invoice_id i
 * BILLING_PERIODS. Det pekar ut EN faktura — firmans egen kvittens for en
 * stangd period — och inte kundforhallandet, och det ar just det talet
 * bokforaren behover for att stamma av Klartids period mot bokforingen. Han
 * har ingen vag till BILLING_PORTAL (MANAGER_ONLY, requires_owner), sa doldes
 * det vore kolumnens egen roll — facit for vad som faktiskt betalades —
 * onabar for den enda som ska anvanda den.
 * Servern SELECTar bara de har kolumnerna; att skicka hela raden och lita pa
 * att klienten inte tittar ar ingen atkomstkontroll.
 */
export type SubscriptionPublicRow = Omit<
  SubscriptionRow,
  'stripe_customer_id' | 'stripe_subscription_id' | 'stripe_price_id'
>;

/**
 * EN ANKARPERIOD (beslut 18) — vad firman faktureras for en manad raknad
 * fran den dag den signade. Hela definitionen av vem som raknas, nar
 * stangningen kors och varfor betalningen sker i forskott star pa
 * billing_period i schema/d1.sql.
 */
export interface BillingPeriodRow {
  company_id: CompanyId;
  /**
   * Ankaret som stangde foregaende period och oppnade den har. Andra halvan
   * av nyckeln, och det tal idempotensnyckeln mot Stripe byggs av.
   */
  period_start: Millis;
  /**
   * Nasta ankare. Klartid raknar aldrig fram det sjalv: talet lases vid
   * stangningen ur ABONNEMANGSRADENS period.end pa den faktura ankaret
   * utloste (lines.data[] med subscription satt) — samma falt som
   * invoice.paid flyttar subscription-kolumnerna med. Gar raden inte att
   * lasa stangs ingen period; se billing_period i schema/d1.sql.
   */
  period_end: Millis;
  /**
   * Antal UPPLAGDA personer med en betald roll (PAID_ROLES) vid stangningen
   * — inte antal som rapporterat nagot. Fryst nar perioden stangdes; raknas
   * aldrig om.
   */
  billed_user_count: number;
  /** billed_user_count * PRICE_PER_USER_ORE, ex moms. EN klumpsumma. */
  amount_ore: Ore;
  closed_at: Millis;
  /**
   * Stripes FAKTURA-id ('in_...'), aldrig ett item-id ('ii_...'/'si_...').
   * Fylls av invoice.paid-webhooken for den period posten bar i
   * metadata[period_start] — den ar facit for vad som faktiskt betalades.
   * NULL sa lange perioden ar stangd men inte betald.
   * DET ENDA STRIPE-ID SOM LAMNAR SERVERN: gar ut i BILLING_PERIODS till
   * FULL_VIEW_ROLES, alltsa aven till bokforaren, som ska kunna stamma av
   * perioden. Det pekar ut en faktura, inte kundforhallandet — se
   * SubscriptionPublicRow for gransen.
   */
  stripe_invoice_id: string | null;
}

export type AuditAction =
  | 'account.created'
  | 'account.updated'
  | 'company.created'
  | 'company.updated'
  | 'member.added'
  | 'member.role_changed'
  | 'member.removed'
  | 'company.ownership_transferred'
  /**
   * AGAREN raderade firman med DELETE_COMPANY. actor_account_id ar HAN —
   * kontot lever vidare, bara firman raderas.
   */
  | 'company.deleted'
  /**
   * GALLRINGEN raderade en firma som statt i laslage i DATA_RETENTION_DAYS
   * (PURGE_CANCELLED_COMPANIES). actor_account_id ar NULL.
   * TVA HANDELSER OCH INTE EN: med en gemensam kod hade den behovt sta bland
   * de aktorslosa i CHECK:en i schema/d1.sql, och da hade en MANNISKAS
   * radering kunnat skrivas utan aktor utan att nagot foll.
   */
  | 'company.purged'
  | 'subscription.status_changed'
  | 'billing_period.closed';

export interface AuditLogRow {
  /**
   * NULL i tva helt olika fall, och bada ar avsiktliga:
   *   1. Handelsen hor inte till en firma (t.ex. 'account.created').
   *   2. Firman ar RADERAD. 'company.deleted' och 'company.purged' skrivs med
   *      company_id NULL och firmans id i detail, for kolumnen bar
   *      ON DELETE CASCADE — en rad som pekade pa firman hade forsvunnit med
   *      den, ocksa skriven forst i samma transaktion. Se RADERINGEN AV EN
   *      FIRMA pa company i schema/d1.sql.
   */
  company_id: CompanyId | null;
  /**
   * NULL = systemkorning utan anvandare: auth 'cron' (periodstangningen,
   * provperiodssvepet, gallringen) och Stripes webhook (auth 'public'). Samma
   * losning som account_op_log.account_id. Ett seedat "systemkonto" i account
   * vore ett konto som gar att begara inloggningslank till.
   * DELETE_COMPANY ar INTE en systemkorning: den har alltid agaren som aktor.
   */
  actor_account_id: AccountId | null;
  action: AuditAction;
  target_account_id: AccountId | null;
  detail: string | null;
  created_at: Millis;
}

export interface AccountOpLogRow {
  op_id: OpId;
  account_id: AccountId | null;
  company_id: CompanyId | null;
  route: string;
  request_path: string;
  request_hash: string;
  response_json: string;
  created_at: Millis;
}

/**
 * UPPSPELNINGSSKYDDET for Stripes webhook, och STRIPE_WEBHOOK:s ENDA
 * idempotens: rutten bar inget op_id (idempotency 'unsafe_no_op_id') och kan
 * darfor inte spela upp ett svar ur account_op_log.
 * STRIPE LEVERERAR OM samma evt_id vid timeout, vid 5xx och vid omleverans
 * fran instrumentpanelen. Raden skrivs DARFOR ALLTID FORST, med INSERT OR
 * IGNORE och i SAMMA transaktion som handelsen behandlas: andrades noll
 * rader ar handelsen redan sedd, och svaret ar 200 utan att nagot mer
 * hander. Fallerar efterarbetet rullas hela transaktionen tillbaka, raden
 * inrymd, och webhooken svarar 5xx sa att Stripe levererar om — annars
 * ligger id:t kvar som "sett" for en handelse som aldrig utfordes. Skrivningen
 * HOS STRIPE ryms inte i transaktionen och skyddas i stallet av
 * stangningens egen idempotensnyckel. Hela regeln star over tabellen i
 * schema/d1.sql.
 */
export interface StripeEventRow {
  id: StripeEventId;
  type: string;
  /** NULL nar handelsen inte gar att knyta till en firma (okand kund). */
  company_id: CompanyId | null;
  received_at: Millis;
}

/* --- 6. Rader i firmans Durable Object --- */

export interface SyncColumns {
  version: Version;
  deleted_at: Millis | null;
  /**
   * Den som SKAPADE raden. Satts nar raden skapas och andras ALDRIG av en
   * senare skrivning — inte av en rattelse, inte av en radering, inte av en
   * aterupplivning; samma ogonblick och samma orubblighet som
   * server_received_at. Svarar pa VEM SOM LADE IN raden, en annan fraga an
   * vem som senast rorde den (updated_by) — slas de ihop gar det inte langre
   * att se att det var chefen som skrev in montorens glomda timmar.
   * FAKTURERINGEN ANVANDER DEN INTE: beslut 18 raknar upplagda personer,
   * aldrig rader.
   */
  created_by: Actor;
  updated_by: Actor;
  device_id: DeviceId | null;
  /**
   * Nar raden FORST nadde servern. EN stampel per SYNC_PUSH-kuvert, delad
   * av varje rad kuvertet skriver — ett kuvert ar EN inlamning, och tio
   * rader ur samma tryck pa Spara ska bara samma ankomsttid, inte tio som
   * skiljer sig at pa millisekunden. Stampeln ANDRAS ALDRIG av en senare
   * skrivning — inte av en rattelse, inte av en radering, inte av en
   * aterupplivning: den svarar pa nar raden FORST kom in, och den fragan har
   * ett enda svar. Nar raden senast rordes ar en annan fraga, som updated_by
   * och seq svarar pa. Vem som skapade raden star i created_by, vem som
   * senast rorde den i updated_by, ordningen i seq.
   */
  server_received_at: Millis;
  seq: Seq;
}

export interface SyncSeqRow {
  id: 1;
  value: Seq;
}

export interface SyncOpLogRow {
  op_id: OpId;
  account_id: AccountId;
  device_id: DeviceId;
  request_hash: string;
  /**
   * SyncOpResult ORDAGRANT som det returnerades — och eftersom bara
   * LYCKADE operationer loggas star det alltid status 'applied' har.
   * UPPSPELAREN byter status till 'duplicate' i farten och lamnar version
   * och seq orerda; loggen ar revisionen av vad som faktiskt hande, inte
   * ett fardigpaketerat svar. Samma princip som account_op_log, dar
   * uppspelningen mintar om bevisen i stallet for att lagra dem.
   */
  result_json: string;
  created_at: Millis;
}

export interface CompanySettingsRow extends SyncColumns {
  id: 1;
  /**
   * KUNDPRIS per mil (10 km), ex moms. 0 = chefen har inte satt firmans
   * milpris an, precis som standardprislistan skapas tom.
   */
  mileage_ore_per_10km: OrePer10Km;
  /** Foljer med underlaget ut. Klartid lagger aldrig pa momsen sjalv. */
  vat_rate_hundredths: Hundredths;
}

export interface MemberRow extends SyncColumns {
  account_id: AccountId;
  email: string;
  name: string;
  role: Role;
  guest_org_number: OrgNumber | null;
  /** 1 = firmans agare (beslut 14). Behovs for att valja mottagare. */
  is_owner: 0 | 1;
}

export type MemberWorkerRow = Omit<
  MemberRow,
  'email' | 'guest_org_number' | 'is_owner'
>;

export interface PriceListRow extends SyncColumns {
  id: PriceListId;
  name: string;
  /** Exakt en levande lista har 1. Den kan aldrig arkiveras. */
  is_default: 0 | 1;
}

export interface PriceRateRow extends SyncColumns {
  id: PriceRateId;
  price_list_id: PriceListId;
  role: PriceRole;
  ore_per_hour: OrePerHour;
}

export interface CustomerRow extends SyncColumns {
  id: CustomerId;
  name: string;
  kind: CustomerKind;
  /** Bara pa foretagskund. Personnummer lagras aldrig. */
  org_number: OrgNumber | null;
  address: string | null;
  price_list_id: PriceListId | null;
  /** ROT-sats, bara privatkund. null = ingen ROT. Klartid raknar aldrig. */
  rot_hundredths: Hundredths | null;
}

export interface WorkOrderRow extends SyncColumns {
  id: WorkOrderId;
  customer_id: CustomerId;
  address: string | null;
  description: string;
  status: WorkOrderStatus;
  /**
   * Bara sortering for manager/worker/apprentice, ALDRIG en
   * behorighetsgrans. For en GAST ar den ett av tre satt att fa se ordern
   * — se work_order i schema/company.sql.
   */
  responsible_account_id: AccountId | null;
  price_list_id: PriceListId | null;
}

/**
 * Tilldelning av en arbetsorder till en INHYRD (beslut 17). Det enda som
 * slapper in en gast pa en order, och darmed det enda som lamnar ut kunden
 * till nagon anstalld i ett annat foretag.
 */
export interface WorkOrderGuestRow extends SyncColumns {
  id: WorkOrderGuestId;
  work_order_id: WorkOrderId;
  account_id: AccountId;
}

export interface WorkOrderPhotoRow extends SyncColumns {
  id: WorkOrderPhotoId;
  work_order_id: WorkOrderId;
  object_key: ObjectKey;
}

export interface TaskRow extends SyncColumns {
  id: TaskId;
  name: string;
  rot_eligible: 0 | 1;
}

/**
 * En invoice_run stamplar varje trafflig rad nar chefen exporterat
 * underlaget. En stamplad rad ar last for alltid (ROW_INVOICED) — enda
 * vagen tillbaka ar att radera hela korningen, vilket nollar stampeln.
 */
export interface InvoiceRunRow extends SyncColumns {
  id: InvoiceRunId;
  from_date: WorkDate;
  to_date: WorkDate;
  customer_id: CustomerId | null;
  work_order_id: WorkOrderId | null;
  note: string | null;
}

/**
 * Tidraden, sedd av chef och bokforare. rate_* och rot_hundredths fryses
 * av servern nar raden sparas (beslut 15); klienten kan inte ens uttrycka
 * dem, se TimeEntryWrite.
 */
export interface TimeEntryRow extends SyncColumns {
  id: TimeEntryId;
  account_id: AccountId;
  work_date: WorkDate;
  minutes: Minutes;
  task_id: TaskId;
  work_order_id: WorkOrderId;
  note: string | null;
  rate_ore_per_hour: OrePerHour;
  rate_role: PriceRole;
  rate_source: RateSource;
  rate_price_list_id: PriceListId;
  /** Fryst ROT-sats. 0 = ingen ROT. Klartid raknar aldrig ut avdraget. */
  rot_hundredths: Hundredths;
  source: EntrySource;
  invoice_run_id: InvoiceRunId | null;
}

/**
 * BESLUT 16: tidraden i montors- och gastlage. Prisfalten finns inte i
 * JSON:en — de goms inte i granssnittet. invoice_run_id ar ocksa borta:
 * invoice_run finns inte i de vyerna, sa pekaren vore en frammande nyckel
 * utan mal och skulle berata exakt vilka rader som redan fakturerats.
 * GRANSEN GALLER AVEN FELSVAREN: ROW_INVOICED bar { table, id } och aldrig
 * invoice_run_id (shared/errors.ts). Kontrollordningen i shared/routes.ts ger
 * med flit en montors offlinetelefon ROW_INVOICED, sa en pekare i det svaret
 * hade lamnat ut i bakvagen precis det som skalas bort har — och for en GAST
 * dessutom ur en fakturakorning i en firma dar han ar anstalld nagon
 * annanstans (beslut 17).
 */
export type TimeEntryWorkerRow = Omit<
  TimeEntryRow,
  | 'rate_ore_per_hour'
  | 'rate_role'
  | 'rate_source'
  | 'rate_price_list_id'
  | 'rot_hundredths'
  | 'invoice_run_id'
>;

/**
 * unit_price_ore ar ett MATERIALPRIS per enhet, inte ett timpris (enheten
 * 'h' finns inte). Den som kopt varan maste se vad den kostade, sa
 * materialrader gar ofiltrerade i montorslage; en GAST far bara sina egna.
 */
export interface MaterialLineRow extends SyncColumns {
  id: MaterialLineId;
  work_order_id: WorkOrderId;
  account_id: AccountId;
  work_date: WorkDate;
  description: string;
  quantity_milli: Milli;
  unit: MaterialUnit;
  unit_price_ore: OrePerUnit;
  invoice_run_id: InvoiceRunId | null;
}

export type MaterialLineWorkerRow = Omit<MaterialLineRow, 'invoice_run_id'>;

export interface MileageRow extends SyncColumns {
  id: MileageId;
  account_id: AccountId;
  work_date: WorkDate;
  meters: Meters;
  work_order_id: WorkOrderId;
  vehicle: Vehicle;
  /** Fryst milersattning. Beslut 16: inte till montors- eller gastlage. */
  rate_ore_per_10km: OrePer10Km;
  invoice_run_id: InvoiceRunId | null;
}

export type MileageWorkerRow = Omit<
  MileageRow,
  'rate_ore_per_10km' | 'invoice_run_id'
>;

export interface ExpenseRow extends SyncColumns {
  id: ExpenseId;
  account_id: AccountId;
  work_date: WorkDate;
  /**
   * EX MOMS. Kvittots totalsumma ar inkl moms och far aldrig skrivas hit:
   * klientens falt heter "Summa exkl. moms" och montoren laser av
   * momsraden. Annars vidarefaktureras varje utlagg 25 % for hogt.
   */
  amount_ore: Ore;
  description: string;
  receipt_object_key: ObjectKey | null;
  /** 1 = vidarefaktureras. 0 = kommer aldrig med i fakturaunderlaget. */
  billable: 0 | 1;
  work_order_id: WorkOrderId | null;
  invoice_run_id: InvoiceRunId | null;
}

export type ExpenseWorkerRow = Omit<ExpenseRow, 'invoice_run_id'>;

export interface AbsenceRow extends SyncColumns {
  id: AbsenceId;
  account_id: AccountId;
  work_date: WorkDate;
  kind: AbsenceKind;
}

export interface WeekStatusRow extends SyncColumns {
  account_id: AccountId;
  iso_week: IsoWeek;
  state: WeekState;
}

export type CustomerWorkerRow = Omit<
  CustomerRow,
  'price_list_id' | 'rot_hundredths'
>;

export type WorkOrderWorkerRow = Omit<WorkOrderRow, 'price_list_id'>;

/* --- 7. Skrivtyper: det klienten faktiskt far skicka. Ingen skrivtyp
 * innehaller prisfalt servern fryser, synkkolumner eller invoice_run_id. */

export interface CustomerWrite {
  name: string;
  kind: CustomerKind;
  org_number: OrgNumber | null;
  address: string | null;
  price_list_id: PriceListId | null;
  rot_hundredths: Hundredths | null;
}

export interface WorkOrderWrite {
  customer_id: CustomerId;
  address: string | null;
  description: string;
  status: WorkOrderStatus;
  responsible_account_id: AccountId | null;
  price_list_id: PriceListId | null;
}

export interface WorkOrderGuestWrite {
  work_order_id: WorkOrderId;
  /**
   * Maste vara en medlem med rollen 'guest', annars VALIDATION_FAILED
   * { field: 'account_id' }.
   */
  account_id: AccountId;
}

export interface WorkOrderPhotoWrite {
  work_order_id: WorkOrderId;
  object_key: ObjectKey;
}

export interface TaskWrite {
  name: string;
  rot_eligible: 0 | 1;
}

export interface PriceListWrite {
  name: string;
  is_default: 0 | 1;
}

export interface PriceRateWrite {
  price_list_id: PriceListId;
  role: PriceRole;
  ore_per_hour: OrePerHour;
}

export interface TimeEntryWrite {
  account_id: AccountId;
  work_date: WorkDate;
  minutes: Minutes;
  task_id: TaskId;
  work_order_id: WorkOrderId;
  note: string | null;
  source: EntrySource;
}

export interface MaterialLineWrite {
  work_order_id: WorkOrderId;
  account_id: AccountId;
  work_date: WorkDate;
  description: string;
  quantity_milli: Milli;
  unit: MaterialUnit;
  unit_price_ore: OrePerUnit;
}

export interface MileageWrite {
  account_id: AccountId;
  work_date: WorkDate;
  meters: Meters;
  work_order_id: WorkOrderId;
  vehicle: Vehicle;
}

export interface ExpenseWrite {
  account_id: AccountId;
  work_date: WorkDate;
  /** EX MOMS, se ExpenseRow.amount_ore. */
  amount_ore: Ore;
  description: string;
  receipt_object_key: ObjectKey | null;
  billable: 0 | 1;
  work_order_id: WorkOrderId | null;
}

export interface AbsenceWrite {
  account_id: AccountId;
  work_date: WorkDate;
  kind: AbsenceKind;
}

export interface CompanySettingsWrite {
  mileage_ore_per_10km: OrePer10Km;
  vat_rate_hundredths: Hundredths;
}

/* --- 8. Synk: EN skrivkanal for all foretagsdata.
 * All andring av foretagsdata gar genom SYNC_PUSH som en batchad utkorg —
 * inga skriv-endpoints per tabell.
 * PROTOKOLLET, ordagrant:
 * - Varje operation kors i en EGEN transaktion, i kuvertets ordning,
 *   TILLSAMMANS med sin kvittorad i sync_op_log. En fallen operation
 *   rullar aldrig tillbaka en tidigare och stoppar inte resten.
 * - Ett giltigt kuvert svarar ALLTID HTTP 200, aven om varje operation
 *   foll. ErrorSpec.http_status galler bara nar HELA anropet faller; i
 *   SyncOpResult.error bars bara code, message_sv och detail.
 * - Idempotens: (op_id, account_id, device_id) + request_hash mot
 *   sync_op_log. Samma nyckel + samma hash -> lagrad version och seq med
 *   status 'duplicate'. Samma nyckel + annan hash -> DUPLICATE_OP. Ett
 *   op_id fran ett annat konto eller en annan enhet -> FORBIDDEN.
 * - Bara LYCKADE operationer loggas; en fallen far skickas om med samma
 *   op_id.
 * - version och seq i ett resultat avser ALLTID raden operationen namngav.
 *   Rader servern skapar pa kopet (week_status vid forsta sparningen,
 *   raderna en invoice_run stamplar) far egna seq och kommer med nasta
 *   pull. En stampling andrar bara invoice_run_id och seq, ALDRIG version
 *   — darfor far en offline-telefon alltid ROW_INVOICED, aldrig
 *   SYNC_CONFLICT.
 * - Push-svaret bar ALDRIG radinnehall; klienten kor alltid en SYNC_PULL
 *   direkt efter en lyckad push. */

export type SyncTable =
  | 'customer'
  | 'work_order'
  | 'work_order_guest'
  | 'work_order_photo'
  | 'task'
  | 'price_list'
  | 'price_rate'
  | 'time_entry'
  | 'material_line'
  | 'mileage'
  | 'expense'
  | 'absence'
  | 'company_settings'
  | 'invoice_run';

export type WeekTransition = 'submit' | 'approve' | 'lock' | 'reopen';

export type SyncPut =
  | { table: 'customer'; id: CustomerId; row: CustomerWrite }
  | { table: 'work_order'; id: WorkOrderId; row: WorkOrderWrite }
  | {
      table: 'work_order_guest';
      id: WorkOrderGuestId;
      row: WorkOrderGuestWrite;
    }
  | {
      table: 'work_order_photo';
      id: WorkOrderPhotoId;
      row: WorkOrderPhotoWrite;
    }
  | { table: 'task'; id: TaskId; row: TaskWrite }
  | { table: 'price_list'; id: PriceListId; row: PriceListWrite }
  | { table: 'price_rate'; id: PriceRateId; row: PriceRateWrite }
  | { table: 'time_entry'; id: TimeEntryId; row: TimeEntryWrite }
  | { table: 'material_line'; id: MaterialLineId; row: MaterialLineWrite }
  | { table: 'mileage'; id: MileageId; row: MileageWrite }
  | { table: 'expense'; id: ExpenseId; row: ExpenseWrite }
  | { table: 'absence'; id: AbsenceId; row: AbsenceWrite }
  | { table: 'company_settings'; id: 1; row: CompanySettingsWrite };

export type SyncDelete =
  | { table: 'customer'; id: CustomerId }
  | { table: 'work_order'; id: WorkOrderId }
  | { table: 'work_order_guest'; id: WorkOrderGuestId }
  | { table: 'work_order_photo'; id: WorkOrderPhotoId }
  | { table: 'task'; id: TaskId }
  | { table: 'price_list'; id: PriceListId }
  | { table: 'price_rate'; id: PriceRateId }
  | { table: 'time_entry'; id: TimeEntryId }
  | { table: 'material_line'; id: MaterialLineId }
  | { table: 'mileage'; id: MileageId }
  | { table: 'expense'; id: ExpenseId }
  | { table: 'absence'; id: AbsenceId }
  | { table: 'invoice_run'; id: InvoiceRunId };

/**
 * base_version = null betyder "raden ska inte finnas"; ett tal betyder
 * "raden ska sta pa exakt den versionen". Stammer det inte: SYNC_CONFLICT
 * med detail { server_version, server_seq }.
 * VERSIONSTRAPPAN (schema/company.sql): put med null ger alltid version 1,
 * varje accepterad skrivning darefter ger version + 1. En telefon kan
 * darfor koa flera andringar av samma rad utan att ha varit online (null,
 * 1, 2, ...); ops slas ALDRIG ihop.
 * MJUKRADERAD RAD raknas som BEFINTLIG i versionsjamforelsen men som
 * obefintlig vid varje referensuppslag: base_version = null mot ett id som
 * finns ger alltid SYNC_CONFLICT, medan base_version = den mjukraderade
 * radens version ATERUPPLIVAR raden. Det ar enda vagen att angra en
 * radering.
 * EN DELETE MOT EN REDAN MJUKRADERAD RAD (base_version = dess version) ar
 * ett tillatet no-op: version + 1, ny seq, deleted_at ORORT. Bara sa kan en
 * telefon som koat tva raderingar av samma rad offline tomma sin utkorg —
 * SYNC_CONFLICT hade lagt en operation som aldrig kan lyckas i "Behover
 * atgard".
 */
export type SyncOpBody =
  | ({ kind: 'put'; base_version: Version | null } & SyncPut)
  | ({ kind: 'delete'; base_version: Version } & SyncDelete)
  | {
      kind: 'week';
      account_id: AccountId;
      iso_week: IsoWeek;
      transition: WeekTransition;
    }
  /**
   * Skapar fakturamarkningen. Operationen bar INGEN base_version, sa
   * uppslaget pa id ar dess enda skydd mot en andra stampling: ett id som
   * REDAN finns i invoice_run ger SYNC_CONFLICT med { server_version,
   * server_seq }, aven om korningen ar mjukraderad. Regeln galler oavsett
   * om op_id star i sync_op_log — omsandning fran en annan enhet, eller
   * efter att kvittoraden stadats efter OP_LOG_RETENTION_DAYS, ar just de
   * fall dar idempotenslogen inte langre kan svara.
   */
  | {
      kind: 'invoice_run';
      id: InvoiceRunId;
      from_date: WorkDate;
      to_date: WorkDate;
      customer_id: CustomerId | null;
      work_order_id: WorkOrderId | null;
      note: string | null;
    };

/**
 * op_id genereras pa klienten nar anvandaren trycker Spara och andras
 * aldrig — det ar hela idempotensen (beslut 09). client_time ingar ALDRIG
 * i request_hash; annars byter en omsandning ur utkorgen hash.
 */
export type SyncOp = {
  op_id: OpId;
  client_time: Millis;
} & SyncOpBody;

export interface SyncPushRequest {
  device_id: DeviceId;
  /**
   * Hogst MAX_OPS_PER_PUSH stycken, i den ordning de ska koras. Varje
   * op_id far forekomma HOGST EN gang i kuvertet: sync_op_log innehaller
   * bara tidigare lyckade operationer, sa tva likadana op_id i samma
   * kuvert har ingen definierad utgang. Tva sadana faller HELA anropet med
   * VALIDATION_FAILED { field: 'ops[].op_id' } och ingenting tillampas.
   */
  ops: readonly SyncOp[];
}

export type SyncOpResult =
  | { op_id: OpId; status: 'applied'; version: Version; seq: Seq }
  | { op_id: OpId; status: 'duplicate'; version: Version; seq: Seq }
  | { op_id: OpId; status: 'failed'; error: ApiErrorBody };

export interface SyncPushResponse {
  results: readonly SyncOpResult[];
  license: LicenseProof;
  server_time: Millis;
}

export interface SyncPullRequest {
  since: Seq;
  /**
   * Hogst SYNC_PULL_MAX_LIMIT, standard SYNC_PULL_DEFAULT_LIMIT; ett storre
   * tal kapas till taket i stallet for att avvisas. Talet raknar rader
   * servern LASER, fore vyns filtrering — aldrig antalet rader som hamnar i
   * changes. Annars maste en pull i montors- eller gastlage skanna hela
   * DO:t for att fylla ett fonster sa fort chefen lagt in 900 prisrader,
   * och next_since (hogsta LASTA seq) skulle inte langre ga att harleda ur
   * samma vanda.
   */
  limit?: number;
}

export interface FullChangeSet {
  company_settings: readonly CompanySettingsRow[];
  member: readonly MemberRow[];
  price_list: readonly PriceListRow[];
  price_rate: readonly PriceRateRow[];
  customer: readonly CustomerRow[];
  work_order: readonly WorkOrderRow[];
  work_order_guest: readonly WorkOrderGuestRow[];
  work_order_photo: readonly WorkOrderPhotoRow[];
  task: readonly TaskRow[];
  invoice_run: readonly InvoiceRunRow[];
  time_entry: readonly TimeEntryRow[];
  material_line: readonly MaterialLineRow[];
  mileage: readonly MileageRow[];
  expense: readonly ExpenseRow[];
  absence: readonly AbsenceRow[];
  week_status: readonly WeekStatusRow[];
}

/**
 * BESLUT 16: montorsvyn (worker, apprentice) saknar company_settings,
 * price_list, price_rate, work_order_guest och invoice_run HELT; kunden och
 * arbetsordern saknar sina prispekare; tid- och milrader saknar prisfalten.
 * Stamdata (member, customer, work_order, work_order_photo, task) gar ut i
 * sin helhet — en anstalld montor far rapportera pa varje levande
 * arbetsorder, sa listan far inte filtreras. Personliga rader (time_entry,
 * mileage, expense, absence, week_status) galler BARA personen sjalv.
 * material_line ar undantaget och gar ut for alla ordrar: montoren maste se
 * vad som redan ar kopt till jobbet, och ett materialpris per enhet ar
 * inget timpris.
 */
export interface WorkerChangeSet {
  member: readonly MemberWorkerRow[];
  customer: readonly CustomerWorkerRow[];
  work_order: readonly WorkOrderWorkerRow[];
  work_order_photo: readonly WorkOrderPhotoRow[];
  task: readonly TaskRow[];
  time_entry: readonly TimeEntryWorkerRow[];
  material_line: readonly MaterialLineWorkerRow[];
  mileage: readonly MileageWorkerRow[];
  expense: readonly ExpenseWorkerRow[];
  absence: readonly AbsenceRow[];
  week_status: readonly WeekStatusRow[];
}

/**
 * BESLUT 17: gasten ar per definition anstalld i ETT ANNAT FORETAG, ofta en
 * konkurrent, och far darfor ALDRIG firmans kundregister, alla
 * arbetsordrar eller andras materialpriser i sin lokala databas.
 * URVAL: customer, work_order och work_order_photo omfattar BARA de ordrar
 * gasten ser enligt work_order i schema/company.sql (levande tilldelning i
 * work_order_guest, ansvarig pa ordern, eller egen levande rad pa den) och
 * kunderna till just dem. material_line galler bara hans EGNA rader. task
 * och member gar ut i sin helhet: momentlistan behovs for att kunna
 * rapportera, och namn plus roll ar inte kansligt. work_order_guest
 * skickas aldrig ut — gasten ser resultatet, inte tilldelningarna.
 */
export interface GuestChangeSet {
  member: readonly MemberWorkerRow[];
  customer: readonly CustomerWorkerRow[];
  work_order: readonly WorkOrderWorkerRow[];
  work_order_photo: readonly WorkOrderPhotoRow[];
  task: readonly TaskRow[];
  time_entry: readonly TimeEntryWorkerRow[];
  material_line: readonly MaterialLineWorkerRow[];
  mileage: readonly MileageWorkerRow[];
  expense: readonly ExpenseWorkerRow[];
  absence: readonly AbsenceRow[];
  week_status: readonly WeekStatusRow[];
}

/**
 * MARKOREN: next_since ar hogsta seq servern LASTE i vandan, aven for
 * rader som filtrerades bort av vyn; klienten skickar tillbaka det som
 * `since`. has_more = true betyder bara att fler seq aterstar — INTE att
 * changes ar icke-tom. En pull som ger noll rader med has_more = true ar
 * normal i montors- och gastlage och ska foljas av en ny pull pa
 * next_since; utan den definitionen loopar en gles klient samma fonster i
 * evighet.
 * VYBYTE: klienten sparar vilken view forra svaret hade. Skiljer sig view
 * fran den sparade MASTE klienten tomma hela sin lokala databas for firman
 * och boria om med since = 0 innan nagot visas — servern skickar aldrig om
 * rader med lagre seq, sa en BEFORDRAD montor skulle annars aldrig fa
 * prisfalten pa gammal historik, och en DEGRADERAD chef skulle behalla
 * alla frysta timpriser i sin IndexedDB trots beslut 16. UTKORGEN TOMMAS
 * ALDRIG vid ett vybyte.
 */
export type SyncPullResponse = {
  since: Seq;
  next_since: Seq;
  has_more: boolean;
  license: LicenseProof;
  server_time: Millis;
} & (
  | { view: 'full'; changes: FullChangeSet }
  | { view: 'worker'; changes: WorkerChangeSet }
  | { view: 'guest'; changes: GuestChangeSet }
);

/* --- 9. Konto, inloggning och bevis. Inloggning med en SEXSIFFRIG
 * ENGANGSKOD i ett mejl; koden ar kortlivad, HARLEDD ur serverns hemlighet
 * och lagras aldrig. Klienten bar sedan TVA Ed25519-signerade bevis,
 * giltiga i LICENSE_TTL_DAYS dagar: AccountProof for auth 'account',
 * LicenseProof for auth 'company' (ett per firma). Inget av dem lagras.
 * Saknat/ogiltigt huvud: UNAUTHENTICATED; utgangen token: LICENSE_EXPIRED.
 *
 * VARFOR EN KOD OCH INTE EN LANK. Inloggningen var en lank i mejlet. Agaren
 * installerade appen pa hemskarmen pa sin iPhone, begarde en lank, tryckte
 * pa den — och hamnade i Safari. Safari och en installerad webbapp delar
 * inte lagring pa iOS, sa hemskarmsappen var fortfarande utloggad, och en
 * lank kan aldrig oppnas "i appen han redan har oppen". En kod kan det: han
 * laser sex siffror i mejlet och skriver in dem dar han star. Han lamnar
 * aldrig appen, och iOS foreslar dessutom koden direkt ur mejlet nar faltet
 * bar autocomplete="one-time-code". Samma monster som Slack och Notion.
 *
 * KODEN LAGRAS INTE, precis som lanken inte gjorde det (schema/d1.sql over
 * account forbjuder en token-, session- och inbjudningstabell). Sex siffror
 * kan inte bara en signatur, men de kan HARLEDAS: servern raknar
 * HMAC-SHA256(hemlighet, e-post | tidsfonster) och tar sex siffror ur
 * resultatet, och raknar om samma sak nar koden kommer tillbaka. Fonstret ar
 * LOGIN_CODE_TTL_MINUTES minuter, och det foregaende fonstrets kod godtas
 * ocksa, sa att en kod som skickas 09:59 inte dor 10:00. Detaljerna star i
 * worker/src/modules/account/login-code.ts.
 *
 * KODEN AR INTE ENGANGS i strikt mening — samma begransning som lanken hade
 * och av samma skal: att branna en kod vid forsta bruket kraver ett lagrat
 * sparbevis. Skyddet ligger i tiden och i sparren: hogst
 * LOGIN_CODE_MAX_ATTEMPTS forsok per adress och minut (RATE_LIMITED), sa att
 * en miljon mojligheter inte gar att ga igenom. */

export interface AuthRequestCodeRequest {
  email: string;
  /**
   * Satts bara nar kontot skapas forsta gangen. Ar e-posten OKAND och name
   * saknas svarar servern VALIDATION_FAILED { field: 'name' } — ett konto
   * skapas aldrig utan namn. Ar e-posten kand ignoreras name; namnet
   * andras med UPDATE_ACCOUNT.
   */
  name?: string;
}

/** Bara att brevet gick ivag. Aldrig koden, aldrig om kontot fanns. */
export interface AuthRequestCodeResponse {
  sent: true;
}

export interface AuthVerifyRequest {
  /** Adressen koden mejlades till. Servern gemenerar den. */
  email: string;
  /**
   * Exakt LOGIN_CODE_DIGITS siffror. Blanksteg (brevet skriver '482 917')
   * far forekomma och tas bort av servern; allt annat ar VALIDATION_FAILED
   * { field: 'code' }. En kod som ar ratt till formen men fel till vardet ar
   * UNAUTHENTICATED — samma svar som for en utgangen kod och for ett konto
   * som inte finns, sa att svaret aldrig blir en orakelfraga.
   */
  code: string;
  device_id: DeviceId;
}

/**
 * TOKENFORMAT — galler BADA bevisen (AccountProof och LicenseProof), sa att
 * klienten kan skrivas utan att lasa serverkoden:
 *   '<payload>.<signatur>', bada delarna base64url UTAN utfyllnad.
 *   payload   = UTF-8-JSON av claims med REKURSIVT SORTERADE nycklar.
 *   signatur  = Ed25519 over payload-STRANGENS bytes (aldrig over den
 *               avkodade JSON:en, och aldrig over nagot annat).
 * Inget JOSE-huvud och ingen alg-forhandling: algoritmen ar Ed25519 och kan
 * darfor inte forhandlas ned till 'none'. Servern slar upp nyckeln pa
 * claims.kid FORE signaturkontrollen, avvisar ett okant kid som
 * UNAUTHENTICATED, och provar aldrig flera nycklar i tur och ordning.
 * AccountProof:s claims bar account_id, kid, issued_at och expires_at;
 * LicenseProof:s bar hela LicenseClaims. Inget av bevisen lagras.
 */
export interface AccountProof {
  token: string;
  expires_at: Millis;
}

export interface LicenseProof {
  company_id: CompanyId;
  token: string;
  expires_at: Millis;
}

export interface LicenseClaims {
  /**
   * Nyckel-id. Servern haller MINST TVA giltiga nycklar samtidigt: nya
   * bevis signeras med den nya, medan bevis utfardade med den gamla
   * verifieras med sin egen i LICENSE_TTL_DAYS dagar till. Utan kid dor
   * varje utestaende 30-dagarsbevis i samma stund nyckeln roteras, och med
   * dem beslut 12 — telefonen som varit utan nat i tre veckor blir utlast
   * med sin utkorg kvar.
   */
  kid: string;
  account_id: AccountId;
  company_id: CompanyId;
  role: Role;
  subscription_status: SubscriptionStatus;
  issued_at: Millis;
  expires_at: Millis;
}

/** Firman som den visas i appen. do_id lamnar aldrig servern. */
export interface CompanySummary {
  id: CompanyId;
  name: string;
  org_number: OrgNumber;
  role: Role;
  is_owner: boolean;
  subscription_status: SubscriptionStatus;
}

export interface MeResponse {
  account: AccountRow;
  account_proof: AccountProof;
  companies: readonly CompanySummary[];
  licenses: readonly LicenseProof[];
}

export type AuthVerifyResponse = MeResponse;

export interface UpdateAccountRequest {
  name: string;
}

/* --- 10. Firma och medlemmar --- */

/**
 * Skaparen blir agare (beslut 14) OCH manager i samma transaktion. Servern
 * gor dessutom, i samma anrop och fore svaret: (1) skapar firmans Durable
 * Object med jurisdiction 'eu', (2) kor schema/company.sql, som seedar
 * company_settings (milpris 0), (3) speglar member-raden for skaparen med
 * is_owner = 1, (4) lagger upp en TOM standardprislista med is_default = 1,
 * (5) skapar en Stripe-kund och skriver subscription-raden med status
 * 'trialing', stripe_subscription_id = null och current_period_end = nu +
 * TRIAL_DAYS. DEN TIDPUNKTEN AR FIRMANS ANKARE (beslut 18): signeras firman
 * den 20:e blir den 27:e forsta dragningen och sedan betalningsdag varje
 * manad. Prislistan ar tom med flit, sa forsta tidraden ger
 * PRICE_MISSING tills chefen fyllt i den. En firma utan subscription-rad
 * kan inte finnas; forsta betalningen tecknas med BILLING_CHECKOUT.
 */
export interface CreateCompanyRequest {
  id: CompanyId;
  name: string;
  org_number: OrgNumber;
  default_price_list_id: PriceListId;
}

export interface CreateCompanyResponse {
  company: CompanySummary;
  license: LicenseProof;
}

/** Rattar firmans namn och org.nr. Upptaget nummer ger ORG_NUMBER_TAKEN. */
export interface UpdateCompanyRequest {
  name: string;
  org_number: OrgNumber;
}

/**
 * TVA GASTFEL, OCH DE KOMMER FRAN OLIKA STALLEN (beslut 17):
 *   GUEST_ORG_NUMBER_MISSING  role ar 'guest' men guest_org_number saknas.
 *                             SERVERN avvisar innan skrivningen, sa att
 *                             beskedet pekar ut vad som fattas i stallet for
 *                             att CHECK ((role = 'guest') = ...) i
 *                             schema/d1.sql ska falla anonymt.
 *   GUEST_ORG_NUMBER_INVALID  numret finns men ar firmans eget, eller en
 *                             firma personen sjalv ager. Det reser
 *                             TRIGGRARNA i schema/d1.sql, ordagrant.
 * Ett guest_org_number pa en ICKE-gast ar VALIDATION_FAILED
 * { field: 'guest_org_number' } — samma CHECK, andra riktningen.
 */
export interface AddMemberRequest {
  email: string;
  name: string;
  role: Role;
  /** Beslut 17: obligatoriskt for gast, aldrig firmans eget nummer. */
  guest_org_number?: OrgNumber;
}

/** Samma tva gastfel som pa AddMemberRequest. */
export interface SetMemberRoleRequest {
  role: Role;
  guest_org_number?: OrgNumber;
}

export type MemberMutationResponse = MembershipRow;

export interface RemoveMemberResponse {
  removed: true;
}

/** Beslut 14: malkontot maste redan vara manager i firman. */
export interface TransferOwnershipRequest {
  account_id: AccountId;
}

export interface TransferOwnershipResponse {
  company: CompanySummary;
}

/**
 * RADERINGEN AV FIRMAN — den farligaste begaran i hela kontraktet, och den
 * enda som inte gar att gora ogjord.
 *
 * Villkoren punkt 10.6 och personuppgiftsbitradesavtalet punkt 12 lovar bada
 * att uppgifterna GAR att radera. Utan den har rutten gick loftet bara att
 * halla genom att nagon oppnade databasen for hand.
 *
 * BEKRAFTELSEN AR FIRMANS ORGANISATIONSNUMMER, och det ar ett val:
 *   - Det ar UNIKT i schema/d1.sql (company.org_number UNIQUE). Firmans NAMN
 *     ar det inte, och en bekraftelse som TVA olika firmor kan uppfylla
 *     bekraftar ingenting — den som ager "Bygg AB" i tva bolag hade kunnat
 *     skriva ratt namn och radera fel firma. Sokvagens :company_id pekar ut
 *     raden; bekraftelsen finns for att fanga att MANNISKAN valde fel rad, och
 *     da maste den vara sarskiljande.
 *   - Det gar inte att fa fram av misstag. Ett dubbeltryck, en tapp i fickan
 *     eller en klient med en trasig knapp kan aldrig producera tio ratta
 *     siffror; formen provas med requireOrgNumber, samma vakt som allt annat.
 *   - SERVERN jamfor, inte klienten. En bekraftelse som bara finns i en dialog
 *     ar ingen sparr — den gar forbi med curl.
 * Fel eller felskrivet nummer ger VALIDATION_FAILED
 * { field: 'confirm_org_number' } och INGENTING raderas. Ingen egen felkod:
 * koden betyder ordagrant att ett namngivet falt i begaran ar felskrivet, och
 * det ar precis vad det ar.
 *
 * KLIENTEN MASTE PAMINNA OM EXPORTEN FORE BEKRAFTELSERUTAN, och kravet star
 * har for att det inte ska kunna glommas nar skarmen byggs. Beslut 11 och
 * villkoren punkt 9.2: exporten ligger ALDRIG bakom betalvagg, och punkt 10.6
 * sager att kunden ska hamta ut det han vill ha kvar innan dess — efterat
 * finns ingenting att hamta. Rutan MASTE alltsa saga tre saker, i den har
 * ordningen, innan faltet ens visas:
 *   1. att fakturaunderlaget gar att ta ut som CSV just nu, gratis, och att
 *      det fungerar aven i laslage,
 *   2. att uppgifterna INTE gar att aterskapa,
 *   3. vilken firma det galler, med namn OCH org.nr, sa att den som skriver
 *      numret ser vad han bekraftar.
 * Bekraftelseknappen namner raderingen och ordet "for alltid"; ingenting
 * mildare. Sjalva formuleringarna skrivs i app/src, som ar den enda plats i
 * bygget dar en svensk text far ha a-ring och prickar.
 */
export interface DeleteCompanyRequest {
  /** MASTE vara exakt company.org_number. Se ovan. */
  confirm_org_number: OrgNumber;
}

/**
 * Ingen papperskorg, ingen angerknapp och inget att lamna tillbaka: firmans
 * rader i D1 ar borta och dess Durable Object ar tomt nar svaret gar ut.
 * Formen ar RemoveMemberResponse:s, av samma skal — det finns ingen rad kvar
 * att beskriva.
 */
export interface DeleteCompanyResponse {
  deleted: true;
}

/* --- 11. Prenumeration och fakturering --- */

export interface SubscriptionResponse {
  /**
   * Utan firmans betalningsidentitet — de tre stripe_*-kolumnerna — se
   * SubscriptionPublicRow (beslut 07). NAR NASTA DRAGNING SKER star har, som
   * subscription.current_period_end: det ar firmans ankare, och det finns
   * inget separat datumfalt i svaret som kan saga nagot annat.
   */
  subscription: SubscriptionPublicRow;
  /**
   * Beslut 18: antal personer som AR UPPLAGDA i firman med en betald roll
   * (PAID_ROLES) JUST NU — inte antal som rapporterat nagot. Raknas med
   * EXAKT samma fraga som stangningen (billing_period i schema/d1.sql),
   * annars visar chefens skarm ett annat tal an fakturan.
   * PRELIMINART bara i den meningen att nagon kan laggas till eller tas bort
   * innan ankaret nas; talet ackumuleras inte under perioden, sa det ar
   * exakt vad nasta dragning blir om laget star still. Beloppet ar
   * current_billed_user_count * price_per_user_ore, ex moms.
   */
  current_billed_user_count: number;
  price_per_user_ore: Ore;
}

export interface BillingCheckoutResponse {
  url: string;
}

export interface BillingPortalResponse {
  url: string;
}

export interface BillingPeriodsResponse {
  periods: readonly BillingPeriodRow[];
}

/**
 * PERIODSTANGNINGEN (beslut 18). NORMALVAGEN IN AR invoice.created: Stripe
 * skapar abonnemangsfakturan i ankarogonblicket och vantar ~1 h innan den
 * slutfors, och det ar i det fonstret engangsposten maste laggas upp for att
 * hamna pa DEN fakturan. Cron-svepet VARJE DYGN ar EFTERSLAPSVAKT for firmor
 * vars invoice.created aldrig kom fram — aldrig en gemensam korning den 1:a,
 * och aldrig i forvag. Vilka firmor svepet traffar star som en fardig fraga
 * pa billing_period i schema/d1.sql; auth ar 'cron' och op_id = de forsta 32
 * tecknen av SHA-256(company_id + ':' + period_start) i hex, sa att en
 * omkorning blir 'duplicate' i stallet for en andra fakturarad (uppslaget i
 * account_op_log ar NULLSAKERT, se schema/d1.sql: systemkorningens
 * account_id ar NULL).
 * BARA ANKARFAKTURAN UTLOSER STANGNINGEN. Stripe skickar invoice.created
 * aven for fakturor som inte ar abonnemangets cykel — en manuell faktura,
 * eller den faktura en efterslapande engangspost dras in pa. Handelsen ror
 * faktureringen bara nar billing_reason ar 'subscription_cycle' (eller
 * 'subscription_create' vid omteckningen efter ett las, som tecknas utan
 * trial_end) OCH abonnemangsradens period.start ar exakt firmans ankare;
 * allt annat kvitteras 200 och ror ingenting. Utan avgransningen startar en
 * manuell faktura en stangning som per konstruktion MASTE falla pa
 * SERVICE_UNAVAILABLE (retry 'retry'), och Stripe sander om handelsen i tre
 * dygn for nagot som aldrig kan lyckas. Hela regeln star pa subscription i
 * schema/d1.sql (AVGRANSNINGEN).
 * TREDJE VAGEN IN: invoice.paid sjalv. STANG FORE FLYTT (subscription i
 * schema/d1.sql) forbjuder ankaret att stega over en ostangd period, sa en
 * betald ankarfaktura vars period saknar rad anropar den har rutten FORST
 * och flyttar periodkolumnerna sedan.
 * VILKA SOM RAKNAS: alla som ar UPPLAGDA med en betald roll (PAID_ROLES) i
 * firmans DO i stangningsogonblicket. Inga rader, ingen rapportering, ingen
 * proportionering. Talet fryses i billing_period och raknas aldrig om.
 * VARIFRAN period_end KOMMER: begaran bar det INTE. Stangningen laser talet
 * ur STRIPE och raknar det aldrig fram sjalv — ABONNEMANGSRADENS period.end
 * pa den faktura ankaret utloste (lines.data[] med subscription satt), samma
 * falt som invoice.paid flyttar periodkolumnerna med. Normalvagen bar
 * invoice.created den fakturan med sig, efterslapsvakten hamtar den som
 * prenumerationens latest_invoice. Gar den inte att lasa, eller bar raden ett
 * annat period.start an period_start, stangs INGEN period: svaret ar
 * SERVICE_UNAVAILABLE (retry 'retry'), inget skrivs, ingen post laggs upp hos
 * Stripe, och cronen tar firman i nasta svep. Rutten ar alltsa ingen ren
 * D1-rakning: utan Stripe-lasningen finns inget period_end.
 */
export interface CloseBillingPeriodRequest {
  company_id: CompanyId;
  /**
   * Ankaret som gick, ms UTC. MASTE vara exakt lika med EN AV TVA kolumner
   * pa firmans egen subscription-rad, annars VALIDATION_FAILED
   * { field: 'period_start' } och ingenting stangs: talet ar bade nyckeln i
   * billing_period och det op_id och Stripes idempotensnyckel byggs av, sa
   * en cron-korning som bar ett foraldrat ankare skulle annars kunna oppna
   * en period som inte finns.
   *   current_period_end    ankaret brann nyss och betalningen har annu inte
   *                         flyttat kolumnerna. Normalvagen, och svepets
   *                         gren (a).
   *   current_period_start  ankaret brann och invoice.paid hann flytta
   *                         kolumnerna innan perioden stangdes. Svepets
   *                         gren (b) — efterslapsvaktens enda vag in i det
   *                         fall den faktiskt finns for.
   * BADA ar ankaren som INTRAFFAT; talet raknas aldrig fram lokalt. Se
   * billing_period i schema/d1.sql for hela villkoret.
   */
  period_start: Millis;
}

export interface CloseBillingPeriodResponse {
  period: BillingPeriodRow;
  subscription_status: SubscriptionStatus;
}

/**
 * Provperiodssvepet. Kors av en Cron Trigger VARJE DYGN: TRIAL_DAYS ar 7
 * raknat fran signeringsdagen, sa provperioder loper ut pa alla veckans
 * dagar och ingen fast dag i kalendern kan fanga dem.
 * SVEPET AR ENDA VAGEN UT UR EN PROVPERIOD SOM ALDRIG FICK ETT KORT.
 * Periodstangningen kan inte gora jobbet: den ror bara firmor som redan har
 * en tecknad prenumeration (status 'active' eller 'past_due'), och en firma
 * som aldrig gjorde BILLING_CHECKOUT har varken cykel eller ankare att
 * stanga. Utan svepet skulle den firman ligga kvar i 'trialing' och arbeta
 * gratis i evighet.
 * Regeln: status 'trialing' med passerad current_period_end -> 'locked',
 * plus audit_log 'subscription.status_changed' (actor_account_id NULL). Rent
 * idempotent — en firma som redan ar 'locked' ror svepet inte — och op_id
 * harleds ur dagen: de forsta 32 tecknen av SHA-256('trial-sweep:' + date) i
 * hex.
 */
export interface LockExpiredTrialsRequest {
  /**
   * Dygnet svepet kors for, Europe/Stockholm. Ger bade op_id och gransen.
   * Market WorkDate ateranvands for formen ('YYYY-MM-DD' i Europe/Stockholm)
   * sa att inget nytt datumbegrepp uppstar; det ar en kalenderdag, inte en
   * arbetsdag pa en rad.
   */
  date: WorkDate;
}

export interface LockExpiredTrialsResponse {
  /** Firmor som gick fran 'trialing' till 'locked' i just den har korningen. */
  locked_company_ids: readonly CompanyId[];
}

/**
 * GALLRINGEN. Kors av en Cron Trigger VARJE DYGN och raderar varje firma som
 * statt i laslage langre an DATA_RETENTION_DAYS — villkoren punkt 10.5 och
 * 10.6: uppgifterna sparas i 12 manader raknat fran den dag den sista betalda
 * perioden tog slut, och darefter raderas de.
 * VARFOR VARJE DYGN och inte en fast dag: uppsagningar sker alla dagar i
 * veckan, precis som provperioder loper ut alla dagar (TRIAL_DAYS raknas fran
 * signeringsdagen). Ingen dag i kalendern kan fanga dem.
 * URVALET star ordagrant pa company i schema/d1.sql: status 'locked' med ett
 * locked_at som gatt ut. EN FIRMA SOM FORTFARANDE KAN KOMMA TILLBAKA GALLRAS
 * ALDRIG — 'trialing', 'active' och 'past_due' star inte i villkoret, och en
 * firma som tecknat om enligt punkt 9.3 lamnar 'locked' och far locked_at
 * NULL i samma sats som statusen.
 * VERKAN AR EXAKT DELETE_COMPANY:S: firmans Durable Object toms forst, sedan
 * raderas company-raden i D1 och kaskaden tar resten. Skillnaden ar aktoren —
 * raden i audit_log ar 'company.purged' med actor_account_id NULL, mot
 * 'company.deleted' med agaren.
 * EN FIRMA SOM FALLER STOPPAR INTE DE ANDRA: gar dess DO inte att tomma
 * lamnas firman ORORD i D1 och tas i nasta svep — urvalet valjer den igen. Att
 * radera D1-raden anda vore att kasta bort den enda pekaren (company.do_id)
 * till uppgifter som da aldrig mer gar att na eller radera.
 * op_id harleds ur dagen: de forsta 32 tecknen av SHA-256('purge-sweep:' +
 * date) i hex, samma form som provperiodssvepets. Tva korningar samma dygn
 * ger darfor samma nyckel, och den andra spelas upp ur account_op_log utan
 * att en enda rad ror sig.
 */
export interface PurgeCancelledCompaniesRequest {
  /**
   * Dygnet gallringen kors for, Europe/Stockholm. Ger op_id och namnger
   * dygnet i loggen; GRANSEN ar korningens eget nu minus
   * DATA_RETENTION_DAYS, precis som provperiodssvepets grans ar dess nu.
   */
  date: WorkDate;
}

export interface PurgeCancelledCompaniesResponse {
  /**
   * Firmor som raderades i just den har korningen. En firma vars Durable
   * Object inte gick att tomma star INTE med — den lamnades orord.
   */
  purged_company_ids: readonly CompanyId[];
}

/* --- 12. Vyer som servern raknar fram --- */

export interface TodayResponse {
  work_date: WorkDate;
  reported: readonly {
    account_id: AccountId;
    name: string;
    /**
     * Summan av dagens tidrader. BESLUT 06: "rapporterat" HAR = minst en
     * levande TIDRAD i dag, oavsett vem som skrev in den — chefen som fyller
     * i at en montor far honom alltsa att synas som rapporterad har. Det ar
     * en ren dagsvy for chefen och har ingenting med faktureringen att gora:
     * beslut 18 raknar UPPLAGDA personer, inte rader, sa ingen rad pa den
     * har skarmen kan gora nagon dyrare eller billigare.
     */
    minutes: Minutes;
  }[];
  not_reported: readonly {
    account_id: AccountId;
    name: string;
    absence: AbsenceKind | null;
  }[];
}

export type InvoicedFilter = 'unbilled' | 'billed' | 'all';

/**
 * GET-rutt: falten kodas som query-parametrar med exakt de har namnen —
 * samma namn som kolumnerna i invoice_run, sa ingen mappning behovs mellan
 * lager. from_date och to_date ar INKLUSIVE och jamfors som strang mot
 * work_date; hogst INVOICE_BASIS_MAX_DAYS dagar. Utelamnad customer_id /
 * work_order_id = inget filter; utelamnad invoiced_filter = 'unbilled'.
 * Okant/raderat customer_id ger UNKNOWN_REFERENCE, okant work_order_id ger
 * UNKNOWN_WORK_ORDER.
 */
export interface InvoiceBasisRequest {
  from_date: WorkDate;
  to_date: WorkDate;
  customer_id?: CustomerId;
  work_order_id?: WorkOrderId;
  invoiced_filter?: InvoicedFilter;
}

/**
 * Beslut 05: "att fakturera" = arbete + material + utlagg som
 * vidarefaktureras + mil. Attesterat och oattesterat redovisas var for sig
 * och summeras aldrig ihop tyst — det finns ingen gemensam total i typen.
 */
export interface InvoiceBasisTotals {
  work_ore: Ore;
  material_ore: Ore;
  expense_ore: Ore;
  mileage_ore: Ore;
  total_ore: Ore;
}

export type InvoiceBasisLine = {
  week_approved: boolean;
  invoice_run_id: InvoiceRunId | null;
} & (
  | {
      kind: 'work';
      id: TimeEntryId;
      work_date: WorkDate;
      account_id: AccountId;
      account_name: string;
      task_id: TaskId;
      task_name: string;
      rot_eligible: 0 | 1;
      minutes: Minutes;
      rate_ore_per_hour: OrePerHour;
      rate_role: PriceRole;
      rate_source: RateSource;
      rot_hundredths: Hundredths;
      amount_ore: Ore;
    }
  | {
      kind: 'material';
      id: MaterialLineId;
      work_date: WorkDate;
      description: string;
      quantity_milli: Milli;
      unit: MaterialUnit;
      unit_price_ore: OrePerUnit;
      amount_ore: Ore;
    }
  | {
      kind: 'expense';
      id: ExpenseId;
      work_date: WorkDate;
      description: string;
      receipt_object_key: ObjectKey | null;
      amount_ore: Ore;
    }
  | {
      kind: 'mileage';
      id: MileageId;
      work_date: WorkDate;
      account_id: AccountId;
      meters: Meters;
      vehicle: Vehicle;
      rate_ore_per_10km: OrePer10Km;
      amount_ore: Ore;
    }
);

export interface InvoiceBasisWorkOrder {
  work_order_id: WorkOrderId;
  description: string;
  customer_id: CustomerId;
  customer_name: string;
  approved: InvoiceBasisTotals;
  pending: InvoiceBasisTotals;
  lines: readonly InvoiceBasisLine[];
}

/**
 * SORTERING, sa att tva exporter i rad ser likadana ut: work_orders efter
 * customer_name, description, work_order_id; lines efter work_date, kind i
 * ordningen work, material, expense, mileage, darefter id. Arbetsordrar
 * utan rader i intervallet utelamnas; ett tomt underlag har work_orders:
 * [] och nollor i bada bucketarna.
 * OGONBLICKSBILD: hela svaret lases i EN synkron vanda i firmans DO;
 * week_status lases EN gang och samma ogonblicksbild avgor week_approved
 * for varje rad. generated_at ar den tidpunkten. Namnuppslag filtrerar
 * aldrig pa deleted_at. Utlagg med billable = 0 finns inte med.
 */
export interface InvoiceBasisResponse {
  from_date: WorkDate;
  to_date: WorkDate;
  invoiced_filter: InvoicedFilter;
  vat_rate_hundredths: Hundredths;
  approved: InvoiceBasisTotals;
  pending: InvoiceBasisTotals;
  work_orders: readonly InvoiceBasisWorkOrder[];
  /**
   * true = fler an INVOICE_BASIS_MAX_LINES rader traffades av intervallet.
   * Summorna i approved och pending ar anda KOMPLETTA for hela intervallet;
   * BARA work_orders (och darmed lines) ar avkortad. Annars skulle chefen
   * kunna fakturera ett for lagt belopp utan att nagot felade. Klienten ber
   * om ett kortare intervall eller en enskild kund for att se raderna.
   */
  lines_truncated: boolean;
  generated_at: Millis;
}

/* --- 13. Filer och rost --- */

/**
 * Bilden skickas base64-kodad i JSON i stallet for multipart eller
 * forsignerad R2-URL: helt typat kontrakt till priset av ~33 % extra data
 * pa mobilt nat. Over MAX_UPLOAD_BYTES avkodade bytes: FILE_TOO_LARGE.
 * object_key genereras PA KLIENTEN (se ObjectKey) sa att raden som pekar pa
 * filen kan laggas i utkorgen aven utan nat. Nyckelns forsta segment MASTE
 * vara sokvagens :company_id, annars VALIDATION_FAILED
 * { field: 'object_key' }; en andra uppladdning till en nyckel som redan
 * finns avvisas ocksa sa — filer skrivs aldrig over.
 */
export interface FileUploadRequest {
  object_key: ObjectKey;
  content_base64: string;
  mime_type: 'image/jpeg' | 'image/webp';
  purpose: 'photo' | 'receipt';
}

export interface FileUploadResponse {
  object_key: ObjectKey;
}

export type BinaryBody = ArrayBuffer;

/**
 * ROSTEN (beslut 02): "du pratar, appen skriver". Ljudet skickas base64-kodat i
 * JSON av samma skal som bilden ovan. mime_type ar det telefonens MediaRecorder
 * kan ge: webm/Opus pa Android, mp4/AAC pa iPhone, ogg/Opus i Firefox. Over
 * MAX_VOICE_AUDIO_BYTES avkodade bytes: FILE_TOO_LARGE { max_bytes }.
 *
 * LJUDET SPARAS ALDRIG, OCH INTE TRANSKRIPTET HELLER. Servern transkriberar,
 * tolkar mot firmans EGNA arbetsordrar och moment, svarar med ett UTKAST och
 * kastar alltsammans. Det enda som nar databasen ar det hantverkaren sjalv
 * bekraftar och sparar — genom SYNC_PUSH som en vanlig tidrad med
 * source = 'voice'. Rutten skriver ingenting.
 *
 * BARA ONLINE, ingen ko (beslut 02): utan nat sager knappen det rakt ut.
 */
export interface VoiceTranscribeRequest {
  audio_base64: string;
  mime_type: 'audio/webm' | 'audio/mp4' | 'audio/ogg';
}

/** De falt i utkastet som tolkningen kan vara osaker pa. Klienten markerar dem. */
export type VoiceDraftField =
  | 'minutes'
  | 'task_ids'
  | 'work_order_id'
  | 'work_date'
  | 'meters'
  | 'vehicle';

/**
 * UTKASTET — samma enheter som resten av kontraktet: minuter, meter, id:n.
 * Varje id ar VERIFIERAT mot firmans egna rader innan det lamnar servern; ett
 * id modellen hittat pa nar aldrig hit.
 *
 * GISSA ALDRIG TYST: hittas ingen arbetsorder som tydligt matchar ar
 * work_order_id null och unmatched.work_order bar det som sades ("Skolvagen"),
 * sa att klienten kan saga "hittade ingen order for Skolvagen — valj sjalv" i
 * stallet for att valja den som liknar mest. Samma for momenten: det som
 * sades men inte passade nagot moment star i unmatched.tasks och foreslas som
 * anteckning, aldrig som ett gissat moment.
 *
 * task_ids OCH INTE task_id: "gravde dagvatten och lastade container" ar TVA
 * moment ur listan, och en tidrad bar ett task_id (TimeEntryWrite), sa
 * klienten delar timmarna pa dem precis som vid manuell inmatning.
 *
 * unsure ar de falt modellen sjalv flaggat, eller som servern inte kunde
 * verifiera (en tidsangivelse utan rast, en order vars namn inte alls
 * forekom i det som sades). Klienten visar dem markerade sa att hantverkaren
 * tittar extra dar. Osakerhet ar ett VARDE i svaret och aldrig en tyst
 * korrigering.
 *
 * work_date null = dagen namndes inte; klienten behaller sin valda dag.
 */
export interface VoiceDraft {
  minutes: Minutes | null;
  task_ids: readonly TaskId[];
  work_order_id: WorkOrderId | null;
  work_date: WorkDate | null;
  meters: Meters | null;
  vehicle: Vehicle | null;
  /** Forslag: det som sades men inte passade nagot moment, plus ovrigt. */
  note: string | null;
  unsure: readonly VoiceDraftField[];
  unmatched: {
    work_order: string | null;
    tasks: readonly string[];
  };
}

export interface VoiceTranscribeResponse {
  /** Transkriptet ORDAGRANT, sa att hantverkaren kan jamfora. Sparas aldrig. */
  text: string;
  draft: VoiceDraft;
}

/* --- 14. Stripe --- */

/**
 * Kroppen ORORD. Signaturen (Stripe-Signature) provas mot den EXAKTA
 * bytestrommen, sa kroppen far aldrig parsas, normaliseras eller
 * serialiseras om innan kontrollen. Faller signaturen svarar rutten 400 och
 * ingenting skrivs — inte heller stripe_event.
 */
export interface StripeWebhookRequest {
  raw: string;
}

export interface StripeWebhookResponse {
  received: true;
}
